import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import DownloadPanel from './components/DownloadPanel';
import StatsPanel from './components/StatsPanel';
import Footer from './components/Footer';
import './styles/app.css';

function App() {
  const [stats, setStats] = useState({
    downloadsToday: 0,
    successRate: 98,
    storageUsed: 1.2
  });

  const [downloadState, setDownloadState] = useState({
    isDownloading: false,
    progress: 0,
    status: 'Ready to download...'
  });

  const [downloadPath, setDownloadPath] = useState('');
  
  // AI Features State
  const [aiFeatures, setAiFeatures] = useState({
    autoRename: false,
    autoTranslateSubs: false,
    autoFolderSort: false,
    cloudSync: false
  });

  useEffect(() => {
    // Load saved download path
    const savedPath = localStorage.getItem('downloadPath');
    if (savedPath) {
      setDownloadPath(savedPath);
    } else {
      // Get the actual download path from Electron
      if (window.electronAPI && window.electronAPI.getDownloadPath) {
        window.electronAPI.getDownloadPath().then(path => {
          setDownloadPath(path);
        });
      } else {
        // Fallback path
        const homeDir = process.env.USERPROFILE || process.env.HOME || 'C:\\Users\\USER';
        setDownloadPath(`${homeDir}\\Downloads\\VideoDownloader`);
      }
    }
  }, []);

  const handleDownloadStart = async (downloadData) => {
    setDownloadState({
      isDownloading: true,
      progress: 0,
      status: 'Starting download...'
    });

    try {
      const result = await window.electronAPI.downloadVideo({
        url: downloadData.url,
        quality: downloadData.quality,
        outputPath: downloadPath,
        options: downloadData.options || {
          downloadSubtitles: false,
          extractAudio: false,
          autoRename: false,
          autoTranslateSubs: false,
          autoFolderSort: false,
          cloudSync: false
        }
      });

      if (result.success) {
        setDownloadState({
          isDownloading: false,
          progress: 100,
          status: 'Download completed!'
        });
        
        // Update stats
        setStats(prev => ({
          ...prev,
          downloadsToday: prev.downloadsToday + 1,
          storageUsed: prev.storageUsed + (downloadData.fileSize || 0.1)
        }));
      } else {
        setDownloadState({
          isDownloading: false,
          progress: 0,
          status: `Error: ${result.message}`
        });
      }
    } catch (error) {
      setDownloadState({
        isDownloading: false,
        progress: 0,
        status: `Error: ${error.message}`
      });
    }
  };

  const handleFolderSelect = async () => {
    try {
      const selectedPath = await window.electronAPI.selectFolder();
      if (selectedPath) {
        setDownloadPath(selectedPath);
        localStorage.setItem('downloadPath', selectedPath);
      }
    } catch (error) {
      console.error('Error selecting folder:', error);
    }
  };

  const handleWindowControl = (action) => {
    switch (action) {
      case 'minimize':
        window.electronAPI.minimizeWindow();
        break;
      case 'maximize':
        window.electronAPI.maximizeWindow();
        break;
      case 'close':
        window.electronAPI.closeWindow();
        break;
      default:
        break;
    }
  };

  return (
    <div className="app">
      {/* Custom Title Bar */}
      <div className="title-bar">
        <div className="title-bar-content">
          <div className="app-title">
            <span className="app-name">Universal Video Downloader Pro v2.0</span>
            <span className="app-tagline">Smart Downloading with AI – Universal Downloader Pro</span>
          </div>
          <div className="window-controls">
            <button 
              className="window-control minimize" 
              onClick={() => handleWindowControl('minimize')}
              title="Minimize"
            >
              −
            </button>
            <button 
              className="window-control maximize" 
              onClick={() => handleWindowControl('maximize')}
              title="Maximize"
            >
              ⧠
            </button>
            <button 
              className="window-control close" 
              onClick={() => handleWindowControl('close')}
              title="Close"
            >
              ×
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        <Sidebar />
        <DownloadPanel 
          onDownloadStart={handleDownloadStart}
          onFolderSelect={handleFolderSelect}
          downloadPath={downloadPath}
          downloadState={downloadState}
          aiFeatures={aiFeatures}
          onAiFeatureToggle={setAiFeatures}
        />
        <StatsPanel stats={stats} />
      </div>

      <Footer />
    </div>
  );
}

export default App;
